import { useCallback, useRef } from 'react';
import { useUrlNavigation } from '../../../hooks';
import { useUrlNavigateCallbacks } from './urlNavigation/useUrlNavigateCallbacks';
import { useAppStartupFromUrl } from './urlNavigation/useAppStartupFromUrl';
import { useUrlWrappedHandlers } from './urlNavigation/useUrlWrappedHandlers';
import { useHistoryNavigation } from './urlNavigation/useHistoryNavigation';

/**
 * Handles URL-based navigation for the App component.
 * Manages initial startup from URL and provides URL-aware navigation handlers.
 */
export function useAppUrlNavigation({
  sprints,
  preferences,
  lastSelectedCampaignId,
  handleSelectSprint,
  handleSelectTemplate,
  handleSelectView,
  handleSidebarNavigate,
  openDocsArea,
  openDashboardsArea,
  openCampaignsHomeArea,
  openSettingsModal,
  setPreferredDetailView,
  setPendingMissionFocus,
  getFirstActiveSprint,
  handleSelectSprintAndUpdateTab,
  handleSelectTemplateAndUpdateTab,
  handleSelectViewAndUpdateTab,
  handleTabClick,
  handleNewTab,
  handleTabViewModeChange,
  syncTabToContent,
  isUpstreamLoading,
  // Startup state is owned by useAppContent so useTabIntegration can read it too
  isInitialStartupComplete,
  setIsInitialStartupComplete,
}) {
  const isNavigatingHistoryRef = useRef(false);
  const startupInProgressRef = useRef(false);

  const urlCallbacks = useUrlNavigateCallbacks({
    isInitialStartupComplete,
    sprints,
    handleSelectSprint,
    handleSelectTemplate,
    handleSelectView,
    handleSidebarNavigate,
    openDocsArea,
    openDashboardsArea,
    openCampaignsHomeArea,
    openSettingsModal,
    setPreferredDetailView,
    setPendingMissionFocus,
    getFirstActiveSprint,
    syncTabToContent,
    handleTabViewModeChange,
    handleTabClick,
  });

  const {
    navigateToCampaign,
    navigateToTemplate,
    navigateToView,
    navigateToDocs,
    navigateToDashboards,
    navigateToCampaigns,
    navigateToNewTab,
    updateViewMode,
    updateCustomViewId,
    clearMissionId,
  } = useUrlNavigation({
    onNavigateToCampaign: urlCallbacks.handleUrlNavigateToCampaign,
    onNavigateToTemplate: urlCallbacks.handleUrlNavigateToTemplate,
    onNavigateToView: urlCallbacks.handleUrlNavigateToView,
    onNavigateToDocs: urlCallbacks.handleUrlNavigateToDocs,
    onNavigateToDashboards: urlCallbacks.handleUrlNavigateToDashboards,
    onNavigateToCampaigns: urlCallbacks.handleUrlNavigateToCampaigns,
    onNavigateToSettings: urlCallbacks.handleUrlNavigateToSettings,
    onNavigateToHome: urlCallbacks.handleUrlNavigateToHome,
    onNavigateToBlank: urlCallbacks.handleUrlNavigateToBlank,
    onViewModeChange: urlCallbacks.handleUrlViewModeChange,
    defaultViewMode: preferences.defaultView || 'flow',
  });

  useAppStartupFromUrl({
    isUpstreamLoading,
    isInitialStartupComplete,
    setIsInitialStartupComplete,
    startupInProgressRef,
    sprints,
    lastSelectedCampaignId,
    getFirstActiveSprint,
    handleSelectSprint,
    handleSelectTemplate,
    handleSelectView,
    handleSidebarNavigate,
    openDocsArea,
    openDashboardsArea,
    setPreferredDetailView,
    handleTabClick,
    navigateToCampaign,
    navigateToTemplate,
    navigateToView,
    navigateToDocs,
    navigateToDashboards,
    navigateToCampaigns,
    navigateToNewTab,
    openCampaignsHomeArea,
    openSettingsModal,
    preferences,
  });

  const wrappedHandlers = useUrlWrappedHandlers({
    preferences,
    openDocsArea,
    openDashboardsArea,
    handleSelectSprintAndUpdateTab,
    handleSelectTemplateAndUpdateTab,
    handleSelectViewAndUpdateTab,
    handleTabClick,
    handleNewTab,
    handleTabViewModeChange,
    navigateToCampaign,
    navigateToTemplate,
    navigateToView,
    navigateToDocs,
    navigateToDashboards,
    navigateToCampaigns,
    navigateToNewTab,
    updateViewMode,
    updateCustomViewId,
    isNavigatingHistoryRef,
  });

  const handleHistoryNavigate = useHistoryNavigation({
    sprints,
    handleSelectSprint,
    handleSelectTemplate,
    handleSelectView,
    handleTabClick,
    navigateToCampaign,
    navigateToTemplate,
    navigateToView,
    navigateToNewTab,
    navigateToDocs,
    navigateToDashboards,
    navigateToCampaigns,
    setPreferredDetailView,
    isNavigatingHistoryRef,
  });

  const resetStartup = useCallback(() => {
    setIsInitialStartupComplete(false);
    startupInProgressRef.current = false;
  }, [setIsInitialStartupComplete]);

  return {
    ...wrappedHandlers,
    handleHistoryNavigate,
    navigateToCampaigns,
    pushCampaignUrl: navigateToCampaign,
    clearMissionId,
    resetStartup,
  };
}
